/**
 * VoyageSettingsModal - Configure voyage settings
 *
 * Settings:
 * - Saved Ports/Locations (favorites for quick selection)
 * - Operation Types (unified list - Phase 2b)
 * - Voyage Purpose Options (unified list - Phase 2b)
 * - Operation Template Editor (Phase 3)
 * - Fuel Capacity (litres)
 * - Voyage Number Format (custom prefix)
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Save,
  Plus,
  Trash2,
  MapPin,
  Ship,
  Fuel,
  Hash,
  Settings,
  Pencil,
  FolderOpen,
  ClipboardList,
  Check
} from 'lucide-react';
import { useStorageState } from '../../../../hooks/useStorageState';
import {
  DEFAULT_OPERATIONS_LIST,
  DEFAULT_PURPOSES_LIST,
  DEFAULT_OPERATION_TEMPLATES,
  DEFAULT_PRE_ARRIVAL_CHECKS,
  DEFAULT_ARRIVAL_CHECKS,
  DEFAULT_PHASE_OPERATIONS
} from '../../../../config/operationsConfig';
import OperationTemplateEditor from './OperationTemplateEditor';
import TemplateBrowserModal from './TemplateBrowserModal';
import VoyagePlansStep from '../../settings/tabs/VoyagePlansStep';

const VoyageSettingsModal = ({ isOpen, onClose }) => {
  // Load saved settings
  const { value: savedSettings, setValue: saveSettings } = useStorageState('voyage_settings', null);

  // Local state
  const [settings, setSettings] = useState({
    // Saved ports/locations
    savedPorts: [],

    // Unified operations list (replaces enabledOperations + customOperations)
    // NOW INCLUDES phase operations (pre_departure, pre_arrival, post_arrival) at the top
    operationsList: [],

    // Unified purposes list (replaces enabledPurposes + customPurposes)
    purposesList: [],

    // Fuel capacity in litres
    fuelCapacity: '',

    // Voyage number format
    voyageNumberPrefix: '',
    voyageNumberStartFrom: 1
  });

  const [newPort, setNewPort] = useState('');
  const [newPurpose, setNewPurpose] = useState('');
  const [newOperation, setNewOperation] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Phase 3: Template editor state
  const [editingOperation, setEditingOperation] = useState(null); // Operation being edited
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);

  // Helper: ensure phase operations are at top of operations list
  const ensurePhaseOperations = (opsList, legacyPreArrivalChecks, legacyPostArrivalChecks) => {
    // Check if phase ops already exist
    const hasPhaseOps = opsList.some(op => op.phaseType);

    if (hasPhaseOps) {
      // Phase ops already exist - just ensure they're at the top
      const phaseOps = opsList.filter(op => op.phaseType);
      const midVoyageOps = opsList.filter(op => !op.phaseType);
      return [...phaseOps, ...midVoyageOps];
    }

    // Need to migrate: create phase ops from defaults, optionally with legacy checks
    const phaseOps = DEFAULT_PHASE_OPERATIONS.map(defaultPhaseOp => {
      // Clone the default
      const phaseOp = { ...defaultPhaseOp, checks: [...(defaultPhaseOp.checks || [])] };

      // Migrate legacy checks if available
      if (defaultPhaseOp.phaseType === 'pre_arrival' && legacyPreArrivalChecks?.length > 0) {
        phaseOp.checks = [...legacyPreArrivalChecks];
      }
      if (defaultPhaseOp.phaseType === 'post_arrival' && legacyPostArrivalChecks?.length > 0) {
        phaseOp.checks = [...legacyPostArrivalChecks];
      }

      return phaseOp;
    });

    return [...phaseOps, ...opsList];
  };

  // Load settings on open
  useEffect(() => {
    if (isOpen && savedSettings) {
      // Migration: if no operationsList, seed from defaults or legacy
      let operationsList = savedSettings.operationsList;
      if (!operationsList || operationsList.length === 0) {
        // Check for legacy customOperations and merge
        if (savedSettings.customOperations && savedSettings.customOperations.length > 0) {
          operationsList = [...DEFAULT_OPERATIONS_LIST, ...savedSettings.customOperations];
        } else {
          operationsList = [...DEFAULT_OPERATIONS_LIST];
        }
      }

      // Ensure phase operations are at top, migrating legacy checks if needed
      operationsList = ensurePhaseOperations(
        operationsList,
        savedSettings.preArrivalChecks,
        savedSettings.postArrivalChecks
      );

      // FT-80: Ensure new default operations (like daily_checks) are added to existing lists
      DEFAULT_OPERATIONS_LIST.forEach(defaultOp => {
        const exists = operationsList.some(op => op.id === defaultOp.id);
        if (!exists) {
          // Insert new default operations before any phase operations
          const phaseOpIndex = operationsList.findIndex(op => op.phaseType);
          if (phaseOpIndex >= 0) {
            operationsList.splice(phaseOpIndex, 0, { ...defaultOp });
          } else {
            operationsList.push({ ...defaultOp });
          }
        }
      });

      // Migration: if no purposesList, seed from defaults or legacy
      let purposesList = savedSettings.purposesList;
      if (!purposesList || purposesList.length === 0) {
        // Start with defaults
        purposesList = [...DEFAULT_PURPOSES_LIST];

        // Check for legacy customPurposes and add them
        if (savedSettings.customPurposes && savedSettings.customPurposes.length > 0) {
          savedSettings.customPurposes.forEach(purpose => {
            purposesList.push({
              id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              label: purpose
            });
          });
        }
      }

      // §A2-FIX-1: Hydrate ONLY the 6 whitelist fields the modal manages locally.
      // voyagePlans is intentionally OMITTED — useVoyagePlans owns that key entirely
      // and writes to storage directly. The merge in handleSave preserves it.
      setSettings({
        savedPorts: savedSettings.savedPorts || [],
        operationsList: operationsList,
        purposesList: purposesList,
        fuelCapacity: savedSettings.fuelCapacity || '',
        voyageNumberPrefix: savedSettings.voyageNumberPrefix || '',
        voyageNumberStartFrom: savedSettings.voyageNumberStartFrom || 1
      });
      setHasChanges(false);
    } else if (isOpen && !savedSettings) {
      // First time - seed with defaults including phase operations
      // Filter phaseType entries from DEFAULT_OPERATIONS_LIST — phase ops live in DEFAULT_PHASE_OPERATIONS only
      const initialOperationsList = [
        ...DEFAULT_PHASE_OPERATIONS,
        ...DEFAULT_OPERATIONS_LIST.filter(op => !op.phaseType)
      ];
      setSettings(prev => ({
        ...prev,
        operationsList: initialOperationsList,
        purposesList: [...DEFAULT_PURPOSES_LIST]
      }));
    }
    // §A2-FIX-1: Dep array is [isOpen] ONLY — NOT [isOpen, savedSettings].
    // useVoyagePlans subscribes to the SAME storage key as a sibling; every hook
    // write rebumps savedSettings reference. Re-firing this effect on those writes
    // would re-hydrate local `settings` from storage and CLOBBER the user's
    // unsaved local edits to the 6 fields (e.g. typed fuelCapacity wiped when the
    // user creates a plan in VoyagePlansStep). ESLint exhaustive-deps complaint
    // is INTENTIONAL — see CODING-BRIEF-A2.md §2 CORRECTION 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Add a saved port
  const handleAddPort = () => {
    if (newPort.trim() && !settings.savedPorts.includes(newPort.trim())) {
      setSettings(prev => ({
        ...prev,
        savedPorts: [...prev.savedPorts, newPort.trim()]
      }));
      setNewPort('');
      setHasChanges(true);
    }
  };

  // Remove a saved port
  const handleRemovePort = (port) => {
    setSettings(prev => ({
      ...prev,
      savedPorts: prev.savedPorts.filter(p => p !== port)
    }));
    setHasChanges(true);
  };

  // Add purpose to unified list
  const handleAddPurpose = () => {
    if (newPurpose.trim()) {
      const exists = settings.purposesList.some(
        p => p.label.toLowerCase() === newPurpose.trim().toLowerCase()
      );
      if (!exists) {
        setSettings(prev => ({
          ...prev,
          purposesList: [
            ...prev.purposesList,
            { id: `custom_${Date.now()}`, label: newPurpose.trim() }
          ]
        }));
        setNewPurpose('');
        setHasChanges(true);
      }
    }
  };

  // Remove purpose from unified list
  const handleRemovePurpose = (purposeId) => {
    setSettings(prev => ({
      ...prev,
      purposesList: prev.purposesList.filter(p => p.id !== purposeId)
    }));
    setHasChanges(true);
  };

  // Toggle purpose enabled/disabled
  const handleTogglePurpose = (purposeId) => {
    setSettings(prev => ({
      ...prev,
      purposesList: prev.purposesList.map(p =>
        p.id === purposeId ? { ...p, enabled: p.enabled === false ? true : false } : p
      )
    }));
    setHasChanges(true);
  };

  // Add operation to unified list
  const handleAddOperation = () => {
    if (newOperation.trim()) {
      const exists = settings.operationsList.some(
        op => op.label.toLowerCase() === newOperation.trim().toLowerCase()
      );
      if (!exists) {
        setSettings(prev => ({
          ...prev,
          operationsList: [
            ...prev.operationsList,
            { id: `custom_${Date.now()}`, label: newOperation.trim() }
          ]
        }));
        setNewOperation('');
        setHasChanges(true);
      }
    }
  };

  // Remove operation from unified list
  const handleRemoveOperation = (operationId) => {
    setSettings(prev => ({
      ...prev,
      operationsList: prev.operationsList.filter(op => op.id !== operationId)
    }));
    setHasChanges(true);
  };

  // Toggle operation enabled/disabled
  const handleToggleOperation = (operationId) => {
    setSettings(prev => ({
      ...prev,
      operationsList: prev.operationsList.map(op =>
        op.id === operationId ? { ...op, enabled: op.enabled === false ? true : false } : op
      )
    }));
    setHasChanges(true);
  };

  // Phase 3: Save operation template (update operation in list)
  const handleSaveOperationTemplate = (updatedOperation) => {
    setSettings(prev => ({
      ...prev,
      operationsList: prev.operationsList.map(op =>
        op.id === updatedOperation.id ? updatedOperation : op
      )
    }));
    setEditingOperation(null);
    setHasChanges(true);
  };

  // Phase 3: Add template from browser
  const handleAddTemplateFromBrowser = (template) => {
    // Check if already exists
    const exists = settings.operationsList.some(op => op.id === template.id);
    if (!exists) {
      setSettings(prev => ({
        ...prev,
        operationsList: [...prev.operationsList, template]
      }));
      setHasChanges(true);
    }
  };

  // Phase 3: Handle edit operation - merge in default template data if missing
  const handleEditOperation = (operation) => {
    // If operation already has fields/checks, use it as-is
    if (operation.fields?.length > 0 || operation.checks?.length > 0) {
      setEditingOperation(operation);
      return;
    }

    // Otherwise, look up the default template and merge in fields/checks
    // Guard against undefined DEFAULT_OPERATION_TEMPLATES
    const defaultTemplate = (DEFAULT_OPERATION_TEMPLATES || []).find(t => t.id === operation.id);
    if (defaultTemplate) {
      setEditingOperation({
        ...operation,
        fields: defaultTemplate.fields || [],
        checks: defaultTemplate.checks || []
      });
    } else {
      // No default template found - just edit with empty fields/checks
      setEditingOperation({
        ...operation,
        fields: [],
        checks: []
      });
    }
  };

  // Save settings
  const handleSave = () => {
    // §A2-FIX-1: Functional-setter merge preserves voyagePlans (managed by the
    // useVoyagePlans hook as a sibling consumer of voyage_settings) and any
    // future fields, while overwriting only the 6 fields managed locally.
    // Because `settings` does NOT contain a voyagePlans key, the spread cannot
    // overwrite prev.voyagePlans — pure merge. See CODING-BRIEF-A2.md §2.
    // useStorageState.setValue supports the function-updater form (verified at
    // useStorageState.js:474-479).
    saveSettings(prev => ({ ...(prev || {}), ...settings }));
    setHasChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return (<>
  {createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Voyage Settings</h2>
                <p className="text-sm text-gray-600 mt-1">Configure voyage defaults and options</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">

            {/* Saved Ports/Locations */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                Saved Ports / Locations
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                Add frequently used ports for quick selection when starting a voyage
              </p>

              {/* Add new port */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newPort}
                  onChange={(e) => setNewPort(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPort()}
                  placeholder="e.g., Southampton, Crew Transfer Platform A"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddPort}
                  disabled={!newPort.trim()}
                  className="px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Saved ports list */}
              {settings.savedPorts.length > 0 ? (
                <div className="space-y-2">
                  {settings.savedPorts.map((port, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                      <span className="text-sm text-gray-700">{port}</span>
                      <button
                        onClick={() => handleRemovePort(port)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No saved ports yet</p>
              )}
            </div>

            {/* Operation Types - Unified List */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Ship className="h-4 w-4 text-gray-500" />
                  Operation Types
                </h4>
                <button
                  onClick={() => setShowTemplateBrowser(true)}
                  className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                >
                  <FolderOpen className="h-3 w-3" />
                  Browse Templates
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Manage operation types available during voyages. Click edit to customize fields and checks.
              </p>

              {/* Operations list */}
              {settings.operationsList.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {/* Phase Operations Section */}
                  {settings.operationsList.some(op => op.phaseType) && (
                    <>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1 pt-1">
                        Phase Checks
                      </div>
                      {settings.operationsList.filter(op => op.phaseType).map((op) => (
                        <div key={op.id} className={`flex items-center justify-between rounded-md px-3 py-2 border-l-2 border-cyan-400 ${op.enabled === false ? 'bg-gray-100' : 'bg-cyan-50'}`}>
                          <div className="flex items-center gap-3">
                            {/* Toggle Switch - only for post_arrival */}
                            {op.phaseType === 'post_arrival' ? (
                              <button
                                onClick={() => handleToggleOperation(op.id)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${op.enabled === false ? 'bg-gray-300' : 'bg-cyan-600'}`}
                                title={op.enabled === false ? 'Enable post-arrival checks' : 'Disable post-arrival checks'}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${op.enabled === false ? 'translate-x-0.5' : 'translate-x-4'}`} />
                              </button>
                            ) : (
                              <div className="w-9" /> /* Spacer for alignment */
                            )}
                            <span className={`text-sm font-medium ${op.enabled === false ? 'text-gray-400' : 'text-cyan-700'}`}>{op.label}</span>
                            <span className="text-xs bg-cyan-100 text-cyan-600 px-1.5 py-0.5 rounded">Required</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditOperation(op)}
                              className="text-cyan-500 hover:text-cyan-700"
                              title="Edit checklist"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            {/* No delete button for permanent phase operations */}
                          </div>
                        </div>
                      ))}

                      {/* Separator between phase ops and mid-voyage ops */}
                      <div className="border-t border-gray-200 my-3" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1">
                        Mid-Voyage Operations
                      </div>
                    </>
                  )}

                  {/* Mid-Voyage Operations */}
                  {settings.operationsList.filter(op => !op.phaseType).map((op) => (
                    <div key={op.id} className={`flex items-center justify-between rounded-md px-3 py-2 ${op.enabled === false ? 'bg-gray-100' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        {/* Toggle Switch */}
                        <button
                          onClick={() => handleToggleOperation(op.id)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${op.enabled === false ? 'bg-gray-300' : 'bg-cyan-600'}`}
                          title={op.enabled === false ? 'Enable operation' : 'Disable operation'}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${op.enabled === false ? 'translate-x-0.5' : 'translate-x-4'}`} />
                        </button>
                        <span className={`text-sm ${op.enabled === false ? 'text-gray-400' : 'text-gray-700'}`}>{op.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditOperation(op)}
                          className="text-gray-400 hover:text-cyan-600"
                          title="Edit template"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {/* Only show delete for non-permanent operations */}
                        {!op.permanent && (
                          <button
                            onClick={() => handleRemoveOperation(op.id)}
                            className="text-gray-400 hover:text-red-500"
                            title="Remove operation"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic mb-3">No operation types configured</p>
              )}

              {/* Add new operation */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newOperation}
                  onChange={(e) => setNewOperation(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddOperation()}
                  placeholder="Add custom operation type..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddOperation}
                  disabled={!newOperation.trim()}
                  className="px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* §A2 — Voyage Plans */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-gray-500" />
                Voyage Plans
              </h4>
              <p className="text-sm text-gray-500 mb-2">
                Build named recipes (waypoints + operation references) for the voyages this vessel does regularly.
              </p>
              <div className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded mb-3 w-fit">
                <Check className="h-3.5 w-3.5" />
                Plans save automatically as you edit — no Save Settings click needed
              </div>
              <VoyagePlansStep voyageSettings={settings} />
            </div>

            {/* Voyage Purpose - Unified List */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <Settings className="h-4 w-4 text-gray-500" />
                Voyage Purpose Options
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                Configure which voyage purposes are available
              </p>

              {/* Purposes list */}
              {settings.purposesList.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {settings.purposesList.map((purpose) => (
                    <div key={purpose.id} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                      <span className={`text-sm ${purpose.enabled !== false ? 'text-gray-700' : 'text-gray-400'}`}>{purpose.label}</span>
                      <div className="flex items-center gap-2">
                        {/* Enable/Disable toggle */}
                        <button
                          onClick={() => handleTogglePurpose(purpose.id)}
                          className={`relative w-10 h-5 rounded-full transition-colors ${purpose.enabled !== false ? 'bg-cyan-500' : 'bg-gray-300'}`}
                          title={purpose.enabled !== false ? 'Enabled - click to disable' : 'Disabled - click to enable'}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${purpose.enabled !== false ? 'left-5' : 'left-0.5'}`} />
                        </button>
                        <button
                          onClick={() => handleRemovePurpose(purpose.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic mb-3">No voyage purposes configured</p>
              )}

              {/* Add custom purpose */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPurpose}
                  onChange={(e) => setNewPurpose(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPurpose()}
                  placeholder="Add custom purpose..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddPurpose}
                  disabled={!newPurpose.trim()}
                  className="px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Fuel Capacity */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <Fuel className="h-4 w-4 text-gray-500" />
                Fuel Capacity
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                Set vessel fuel capacity for fuel tracking calculations
              </p>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={settings.fuelCapacity}
                  onChange={(e) => {
                    setSettings(prev => ({ ...prev, fuelCapacity: e.target.value }));
                    setHasChanges(true);
                  }}
                  placeholder="e.g., 2000"
                  className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">litres</span>
              </div>
            </div>

            {/* NOTE: Pre-Arrival and Post-Arrival checks are now managed via Phase Operations above */}

            {/* Voyage Number Format */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <Hash className="h-4 w-4 text-gray-500" />
                Voyage Number Format
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                Customize how voyage numbers are generated
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Prefix (optional)</label>
                  <input
                    type="text"
                    value={settings.voyageNumberPrefix}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, voyageNumberPrefix: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="e.g., WB-2024-"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start From</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.voyageNumberStartFrom}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, voyageNumberStartFrom: parseInt(e.target.value) || 1 }));
                      setHasChanges(true);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Preview */}
              <p className="text-sm text-gray-500 mt-2">
                Preview: <span className="font-mono text-cyan-600">
                  {settings.voyageNumberPrefix}{String(settings.voyageNumberStartFrom || 1).padStart(3, '0')}
                </span>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                  hasChanges
                    ? "text-white bg-cyan-600 hover:bg-cyan-700"
                    : "text-gray-400 bg-gray-100 border border-gray-300 cursor-not-allowed"
                }`}
              >
                <Save className="h-4 w-4" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  )}

  {/* Phase 3: Template Editor Modal */}
  <OperationTemplateEditor
    isOpen={editingOperation !== null}
    onClose={() => setEditingOperation(null)}
    operation={editingOperation}
    onSave={handleSaveOperationTemplate}
  />

  {/* Phase 3: Template Browser Modal */}
  <TemplateBrowserModal
    isOpen={showTemplateBrowser}
    onClose={() => setShowTemplateBrowser(false)}
    existingOperations={settings.operationsList}
    onAddTemplate={handleAddTemplateFromBrowser}
  />
  </>);
};

export default VoyageSettingsModal;
